'use client';

import { useEffect, useMemo, useState } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { safeLocalStorage } from '@/lib/utils';
import type { CreatorExperienceId } from './experience-config';
import { getCreatorGuideSteps, getCreatorGuideStorageKey } from './creator-onboarding';

export function CreatorOnboardingTour({ experience }: { experience: CreatorExperienceId | null }) {
	const [open, setOpen] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const steps = useMemo(() => (experience ? getCreatorGuideSteps(experience) : []), [experience]);
	const complete = () => {
		if (experience) safeLocalStorage.setItem(getCreatorGuideStorageKey(experience), 'complete');
		setOpen(false);
		setStepIndex(0);
	};

	useEffect(() => {
		if (!experience) return;
		if (safeLocalStorage.getItem(getCreatorGuideStorageKey(experience)) === 'complete') return;
		setStepIndex(0);
		setOpen(true);
	}, [experience]);

	if (!experience || !steps.length) return null;
	const current = steps[stepIndex]!;
	const isLastStep = stepIndex === steps.length - 1;

	return (
		<>
			<button
				type="button"
				onClick={() => {
					setStepIndex(0);
					setOpen(true);
				}}
				className="ml-auto mb-4 flex items-center gap-1.5 text-sm font-medium text-slate-600 transition hover:text-slate-950"
			>
				<QuestionMarkCircleIcon className="size-4" />
				Show guide
			</button>
			<Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && complete()}>
				<DialogContent
					className="max-w-md overflow-hidden border-slate-200 p-0"
					showCloseButton={false}
				>
					<div className="border-b border-slate-100 bg-slate-950 px-6 py-5 text-white">
						<p className="text-sm font-medium text-slate-300">Ekutu QR creator guide</p>
						<div
							className="mt-3 flex gap-1.5"
							aria-label={`Step ${stepIndex + 1} of ${steps.length}`}
						>
							{steps.map((step, index) => (
								<span
									key={step.title}
									className={`h-1 flex-1 rounded-full ${index <= stepIndex ? 'bg-white' : 'bg-slate-700'}`}
								/>
							))}
						</div>
					</div>
					<div className="p-6">
						<DialogHeader>
							<DialogTitle className="text-2xl text-slate-950">{current.title}</DialogTitle>
							<DialogDescription className="pt-1 text-base leading-7 text-slate-600">
								{current.description}
							</DialogDescription>
						</DialogHeader>
						<p className="mt-8 text-sm font-medium text-slate-500">
							{stepIndex + 1} of {steps.length}
						</p>
						<DialogFooter className="mt-3 sm:justify-between">
							<Button type="button" variant="ghost" onClick={complete}>
								Skip guide
							</Button>
							<div className="flex gap-2">
								{stepIndex > 0 && (
									<Button
										type="button"
										variant="outline"
										onClick={() => setStepIndex((value) => value - 1)}
									>
										Back
									</Button>
								)}
								<Button
									type="button"
									onClick={() => (isLastStep ? complete() : setStepIndex((value) => value + 1))}
								>
									{isLastStep ? 'Start creating' : 'Continue'}
								</Button>
							</div>
						</DialogFooter>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
